import { UseFormReturn } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { PricingPlanObjectFormSchemaValues } from 'src/features/pricing/sites/utils/formSchema';
import { pricingPlanMessages } from 'src/features/pricing/messages';
import CalendarWithTime from 'src/registry/components/inputs/CalendarWithTime';
import { Form, FormControl, FormControlStack, FormField, FormItem, FormLabel, FormMessage } from 'src/registry/ui/Form';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import { PRICING_PLAN_STATUS } from 'src/features/pricingPlan/utils/types';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'src/registry/ui/Select';
import { useParking } from 'src/providers/ParkingProvider';

type PricingPlanFormProps = {
    form: UseFormReturn<PricingPlanObjectFormSchemaValues>;
};

function ObjectPricingPlanForm({ form }: PricingPlanFormProps) {
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    // Standard (default) plans are reserved for copying only, so exclude them from selection here.
    // Only published plans can be assigned to an object; drafts are not selectable.
    const { data } = useListPricingPlans({
        currently_active: true,
        is_system: false,
        status: PRICING_PLAN_STATUS.PUBLISHED,
        parkingId,
    });

    return (
        <Form form={form} variant="dialog">
            <FormField
                control={form.control}
                name="pricing_plan_identity"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            <FormattedMessage
                                id={pricingPlanMessages.pricingPlan.id}
                                defaultMessage={pricingPlanMessages.pricingPlan.defaultMessage}
                            />
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <Select onValueChange={field.onChange} value={field.value}>
                                    <SelectTrigger className="w-full">
                                        <SelectValue
                                            placeholder={`${formatMessage(pricingPlanMessages.pricingPlan)}...`}
                                        />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {data?.results.map((option) => (
                                            <SelectItem key={option.id} value={option.identity}>
                                                {
                                                    option.translations.find(
                                                        (translation) => translation.language === locale,
                                                    )?.name
                                                }
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_from"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog" required>
                            {formatMessage(pricingPlanMessages.validFrom)}
                        </FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
            <FormField
                control={form.control}
                name="valid_to"
                render={({ field }) => (
                    <FormItem variant="dialog">
                        <FormLabel variant="dialog">{formatMessage(pricingPlanMessages.validTo)}</FormLabel>
                        <FormControlStack>
                            <FormControl>
                                <CalendarWithTime dateISO={field.value ?? ''} onChange={field.onChange} />
                            </FormControl>
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                )}
            />
        </Form>
    );
}

export default ObjectPricingPlanForm;
